import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '../../utils/cn';
import { toast } from './toast';

export interface CopyButtonProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'onCopy' | 'children' | 'value'
  > {
  /** The value to copy. Resolved lazily at click-time. */
  value: string | (() => string);
  /** Accessible label announced by screen readers. Also used as the tooltip title. */
  label?: string;
  /** Toast message shown after a successful copy. Set to `false` to suppress the toast. */
  toastMessage?: string | false;
  /** Tone of the icon. Defaults to muted neutral. */
  tone?: 'neutral' | 'brand' | 'inverted';
  /** Icon-only buttons default to a 28×28 hit target; set `inline` to render as a small leading icon. */
  inline?: boolean;
  /** Trailing label to render next to the icon (for text-and-icon buttons). */
  children?: React.ReactNode;
  /** Fired after a successful copy. */
  onCopied?: (value: string) => void;
}

const toneClasses: Record<NonNullable<CopyButtonProps['tone']>, string> = {
  neutral:
    'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 ' +
    'dark:text-neutral-400 dark:hover:text-neutral-100 dark:hover:bg-neutral-800',
  brand:
    'text-brand-600 hover:text-brand-700 hover:bg-brand-50 ' +
    'dark:text-brand-400 dark:hover:text-brand-300 dark:hover:bg-brand-900/20',
  inverted:
    'text-white/70 hover:text-white hover:bg-white/10 ' +
    'dark:text-white/70 dark:hover:text-white dark:hover:bg-white/10',
};

async function writeClipboard(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback for older Safari / non-secure contexts
  if (typeof document === 'undefined') throw new Error('Clipboard unavailable');
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
}

/**
 * Small, accessible copy-to-clipboard affordance. Use next to any textual ID,
 * share link, API key, or document reference. Handles:
 *
 *  - async navigator.clipboard with textarea fallback
 *  - 2s "copied" state with Check icon
 *  - optional toast (on by default)
 *  - keyboard + screen-reader friendly (aria-live polite status)
 */
export const CopyButton = forwardRef<HTMLButtonElement, CopyButtonProps>(
  function CopyButton(
    {
      value,
      label = 'Copy',
      toastMessage = 'Copied to clipboard',
      tone = 'neutral',
      inline = false,
      className,
      children,
      onCopied,
      ...rest
    },
    ref
  ) {
    const [copied, setCopied] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }, []);

    const copy = useCallback(async () => {
      const resolved = typeof value === 'function' ? value() : value;
      if (!resolved) return;
      try {
        await writeClipboard(resolved);
        setCopied(true);
        if (toastMessage !== false) toast.success(toastMessage);
        onCopied?.(resolved);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error('Could not copy — clipboard unavailable');
      }
    }, [value, toastMessage, onCopied]);

    const hasLabel = children !== undefined && children !== null && children !== false;

    return (
      <button
        ref={ref}
        type="button"
        onClick={copy}
        aria-label={copied ? 'Copied' : label}
        title={copied ? 'Copied' : label}
        className={cn(
          'inline-flex items-center justify-center rounded-md transition-colors duration-150 ease-out',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2',
          'dark:focus-visible:ring-offset-neutral-900',
          hasLabel ? 'gap-1.5 h-7 px-2 text-xs' : inline ? 'h-6 w-6' : 'h-7 w-7',
          toneClasses[tone],
          className
        )}
        {...rest}
      >
        {copied ? (
          <Check className={inline ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden="true" />
        ) : (
          <Copy className={inline ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden="true" />
        )}
        <span role="status" aria-live="polite" className="sr-only">
          {copied ? 'Copied to clipboard' : ''}
        </span>
        {hasLabel ? <span>{children}</span> : null}
      </button>
    );
  }
);

export default CopyButton;
